/** @typedef {import("../../api/types").ProductsPage} ProductsPage */
import React, {useEffect, useState} from "react";
import axios from "axios";
import ClickableRow from "../../shared/components/ClickableRow";
import {Link} from "react-router-dom";
import SearchBox from "../../shared/components/SearchBox";
import ServiceItemStatusBadge, {STATUS_COLORS} from "../serviceItems/components/ServiceItemStatusBadge";
import ServiceItemStatusFilter from "../serviceItems/components/ServiceItemStatusFilter";
import SortableTh from "../../shared/components/SortableTh";
import {useDebouncedSearch} from "../../hooks/useDebouncedSearch";
import {useListParams} from "../../hooks/useListParams";
import {useScrollRestore} from "../../hooks/useScrollRestore";
import {formatMoney} from "../../shared/utils/format";
import {DEFAULT_ITEM_STATUS_FILTER, SERVICE_ITEM_STATUSES} from "../serviceItems/utils/serviceItemStatus";
import {Alert, Button, Container, Spinner, Table} from "reactstrap";
import IconLabel from "../../shared/components/IconLabel";
import {ChevronLeftIcon, ChevronRightIcon, PlusIcon} from "../../shared/components/icons";

const PRODUCTS_URL = "/os/api/products/";
const DEFAULT_ORDERING = "name"; // by name, A to Z
const STATUS_CODES = SERVICE_ITEM_STATUSES.map((s) => s.code);

const Products = () => {
    // The list's state lives in the address, so it survives a visit to an product.
    // `statuses`: none selected means no filter, every product. `ordering` is the
    // API's value: a field, "-" in front for descending.
    const {page, search, ordering, statuses, setPage, setSearch, setOrdering, setStatuses} = useListParams({
        basePath: "/products",
        defaultOrdering: DEFAULT_ORDERING,
        statusCodes: STATUS_CODES,
        defaultStatuses: DEFAULT_ITEM_STATUS_FILTER,
    });
    // The request waits for a pause in typing; a new search starts from page 1.
    const {text: searchText, setText: setSearchText} = useDebouncedSearch(search, setSearch);
    const [data, setData] = useState(/** @type {ProductsPage|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get(PRODUCTS_URL, {
                params: {page, status: statuses, search: search || undefined, ordering},
                // ?status=AC&status=IN (not status[]=AC), as the API expects
                paramsSerializer: {indexes: null},
                signal: controller.signal,
            })
            .then(({data}) => setData(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar os produtos.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [page, statuses, search, ordering]);

    useScrollRestore(!loading);

    const sort = {ordering, onChange: setOrdering};

    const emptyMessage = search
        ? `Nenhum produto encontrado para “${search}”.`
        : statuses.length
          ? "Nenhum produto com essa situação."
          : "Nenhum produto encontrado.";

    const products = data?.results ?? [];
    const pagination = data?.pagination;

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="d-flex flex-wrap align-items-center flex-grow-1 gap-2">
                    <ServiceItemStatusFilter selected={statuses} onChange={setStatuses} />
                    <SearchBox
                        value={searchText}
                        onChange={setSearchText}
                        placeholder="Buscar por nome ou descrição"
                        ariaLabel="Buscar produtos"
                    />
                </div>
                <Button color="primary" tag={Link} to="/products/new">
                    <IconLabel icon={<PlusIcon />}>Criar</IconLabel>
                </Button>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            <Table hover responsive className="table-cards">
                <thead>
                    <tr>
                        <SortableTh field="name" sort={sort}>
                            Nome
                        </SortableTh>
                        <th>Descrição</th>
                        <th className="d-none d-md-table-cell">Situação</th>
                        <SortableTh field="base_value" sort={sort} className="text-end">
                            Valor base
                        </SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr>
                            <td colSpan={4} className="text-center">
                                <Spinner size="sm" />
                            </td>
                        </tr>
                    )}
                    {!loading && !error && products.length === 0 && (
                        <tr>
                            <td colSpan={4} className="text-center text-muted">
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                    {!loading &&
                        products.map((product) => (
                            <ClickableRow
                                key={product.id}
                                className={`row-stripe row-stripe--${STATUS_COLORS[product.status] ?? "secondary"}`}
                                to={`/products/${product.id}`}
                            >
                                <td>
                                    <Link to={`/products/${product.id}`}>{product.name}</Link>
                                </td>
                                <td>{product.description}</td>
                                <td className="d-none d-md-table-cell">
                                    <ServiceItemStatusBadge status={product.status} />
                                </td>
                                <td className="text-end">{formatMoney(product.base_value, product.currency)}</td>
                            </ClickableRow>
                        ))}
                </tbody>
            </Table>
            {pagination && pagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.prev_page}
                        onClick={() => setPage(pagination.prev_page)}
                    >
                        <IconLabel icon={<ChevronLeftIcon />}>Anterior</IconLabel>
                    </Button>
                    <span>
                        Página {pagination.curr_page} de {pagination.total_pages}
                    </span>
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.next_page}
                        onClick={() => setPage(pagination.next_page)}
                    >
                        <IconLabel icon={<ChevronRightIcon />}>Próxima</IconLabel>
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default Products;
